// Service: Virtual Advisor Q&A Pair API

import axios from 'axios';
import { getAxiosBaseURL } from '../config/apiConfig';
import type {
  QAPair,
  QAPairCreateData,
  QAPairUpdateData,
  QAPairListParams,
  QAPairStatistics,
  SuggestedQAPair
} from '../types/qaPair';

const API_BASE_URL = getAxiosBaseURL();
const BASE_URL = `${API_BASE_URL}/api/v1/virtual-advisor/qa-pairs`;

// Get auth token from localStorage
const getAuthHeaders = () => {
  const token = localStorage.getItem('quanly_truonghoc_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const qaPairService = {
  /**
   * List Q&A pairs with pagination and filters
   */
  async list(params: QAPairListParams = {}) {
    const response = await axios.get<{
      success: boolean;
      data: {
        items: QAPair[];
        pagination: {
          total: number;
          page: number;
          limit: number;
          totalPages: number;
        };
      };
    }>(BASE_URL, {
      params,
      headers: getAuthHeaders()
    });
    return response.data;
  },

  /**
   * Get single Q&A pair by ID
   */
  async getById(id: number) {
    const response = await axios.get<{ success: boolean; data: QAPair }>(
      `${BASE_URL}/${id}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  /**
   * Create new Q&A pair
   */
  async create(data: QAPairCreateData) {
    const response = await axios.post<{
      success: boolean;
      pairId?: number;
      message: string;
    }>(BASE_URL, data, { headers: getAuthHeaders() });
    return response.data;
  },

  /**
   * Update existing Q&A pair
   */
  async update(id: number, data: QAPairUpdateData) {
    const response = await axios.put<{ success: boolean; message: string }>(
      `${BASE_URL}/${id}`,
      data,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  /**
   * Delete Q&A pair
   */
  async delete(id: number) {
    const response = await axios.delete<{ success: boolean; message: string }>(
      `${BASE_URL}/${id}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  /**
   * Approve Q&A pair
   */
  async approve(id: number) {
    const response = await axios.post<{ success: boolean; message: string }>(
      `${BASE_URL}/${id}/approve`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  /**
   * Reject Q&A pair
   */
  async reject(id: number) {
    const response = await axios.post<{ success: boolean; message: string }>(
      `${BASE_URL}/${id}/reject`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  /**
   * Get statistics
   */
  async getStatistics() {
    const response = await axios.get<{ success: boolean; data: QAPairStatistics }>(
      `${BASE_URL}/statistics`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  /**
   * Get suggested Q&A pairs from chat history
   */
  async getSuggestions(params: { minHelpfulness?: number; limit?: number } = {}) {
    const response = await axios.get<{ success: boolean; data: SuggestedQAPair[] }>(
      `${BASE_URL}/suggestions/from-chat-history`,
      {
        params,
        headers: getAuthHeaders()
      }
    );
    return response.data;
  },

  /**
   * Generate embedding for Q&A pair
   */
  async generateEmbedding(id: number) {
    const response = await axios.post<{ success: boolean; message: string }>(
      `${BASE_URL}/${id}/generate-embedding`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  /**
   * Get accuracy metrics for analytics dashboard
   */
  async getAccuracyMetrics(days: number = 30) {
    const response = await axios.get<{ success: boolean; data: any }>(
      `${BASE_URL}/analytics/metrics`,
      {
        params: { days },
        headers: getAuthHeaders()
      }
    );
    return response.data;
  },

  /**
   * Get top Q&A pairs by usage
   */
  async getTopQAPairs(limit: number = 10) {
    const response = await axios.get<{ success: boolean; data: any[] }>(
      `${BASE_URL}/analytics/top-pairs`,
      {
        params: { limit },
        headers: getAuthHeaders()
      }
    );
    return response.data;
  },

  /**
   * Get feedback statistics
   */
  async getFeedbackStats(days: number = 30) {
    const response = await axios.get<{ success: boolean; data: any }>(
      `${BASE_URL}/analytics/feedback-stats`,
      {
        params: { days },
        headers: getAuthHeaders()
      }
    );
    return response.data;
  }
};

export default qaPairService;
